<template>
  <section class="mt-6 text-left" dir="ltr">
    <div class="flex items-center justify-between mb-2">
      <h3 class="text-base md:text-lg font-semibold flex items-center gap-2">
        <Icon icon="heroicons-outline:adjustments-horizontal" class="w-5 h-5" />
        {{ t('aiChatAgentSetup.advanced.responseFiltersTitle', 'Response Filters') }}
      </h3>
      <button type="button" class="btn" @click="addFilter">
        {{ t('aiChatAgentSetup.advanced.responseFiltersAdd', 'Add filter') }}
      </button>
    </div>
    <p class="text-sm text-gray-500 mb-4">
      {{ t('aiChatAgentSetup.advanced.responseFiltersDescription', 'Post-process outgoing responses with multiple filter nodes.') }}
    </p>

    <div v-if="filtersState.length === 0" class="text-sm text-gray-400 border border-dashed border-base-300 rounded-box p-4">
      {{ t('aiChatAgentSetup.advanced.responseFiltersEmpty', 'No filters added yet.') }}
    </div>

    <div v-else class="grid grid-space-3">
      <div
        v-for="(filterNode, idx) in filtersState"
        :key="idx"
        class="p-4 border border-base-300 rounded-box bg-white"
      >
        <div class="grid grid-space-3">
          <div class="flex items-center justify-between">
            <span class="text-sm font-medium">{{ t('aiChatAgentSetup.advanced.responseFiltersNode', 'Node') }} {{ idx + 1 }}</span>
            <button type="button" class="btn" @click="removeFilter(idx)">
              {{ t('common.remove', 'Remove') }}
            </button>
          </div>

          <div class="form-control">
            <label class="label">
              <span class="label-text">{{ t('aiChatAgentSetup.advanced.responseFilterTypeLabel', 'Type') }}</span>
            </label>
            <select
              class="select select-bordered"
              :value="filterNode.type"
              @change="onFilterTypeChange(idx, $event.target.value)"
              aria-label="Response filter type"
            >
              <option value="removeString">{{ t('aiChatAgentSetup.advanced.responseFilterTypeRemoveString', 'Remove Text') }}</option>
              <option v-if="allowReplaceStringFilter === true" value="replaceString">{{ t('aiChatAgentSetup.advanced.responseFilterTypeReplaceString', 'Replace String by String') }}</option>
              <option value="removeRegex">{{ t('aiChatAgentSetup.advanced.responseFilterTypeRemoveRegex', 'Remove Regex Match') }}</option>
            </select>
          </div>

          <div v-if="filterNode.type === 'removeString'" class="form-control">
            <label class="label">
              <span class="label-text">{{ t('aiChatAgentSetup.advanced.responseFilterStringLabel', 'Text') }}</span>
            </label>
            <input
              type="text"
              class="input input-bordered"
              :value="filterNode.text"
              @input="onFilterTextChange(idx, $event.target.value)"
              :placeholder="t('aiChatAgentSetup.advanced.responseFilterStringPlaceholder', 'e.g. /*')"
              aria-label="Text to remove"
            />
          </div>

          <template v-else-if="filterNode.type === 'replaceString'">
            <div class="form-control">
              <label class="label">
                <span class="label-text">{{ t('aiChatAgentSetup.advanced.responseFilterReplaceFromLabel', 'From string') }}</span>
              </label>
              <input
                type="text"
                class="input input-bordered"
                :value="filterNode.replaceFrom"
                @input="onFilterReplaceFromChange(idx, $event.target.value)"
                :placeholder="t('aiChatAgentSetup.advanced.responseFilterReplaceFromPlaceholder', 'e.g. foo')"
                aria-label="String to replace"
              />
            </div>
            <div class="form-control">
              <label class="label">
                <span class="label-text">{{ t('aiChatAgentSetup.advanced.responseFilterReplaceToLabel', 'To string') }}</span>
              </label>
              <input
                type="text"
                class="input input-bordered"
                :value="filterNode.replaceTo"
                @input="onFilterReplaceToChange(idx, $event.target.value)"
                :placeholder="t('aiChatAgentSetup.advanced.responseFilterReplaceToPlaceholder', 'e.g. bar')"
                aria-label="Replacement string"
              />
            </div>
          </template>

          <template v-else>
            <div class="form-control">
              <label class="label">
                <span class="label-text">{{ t('aiChatAgentSetup.advanced.responseFilterRegexPatternLabel', 'Regex pattern') }}</span>
              </label>
              <input
                type="text"
                class="input input-bordered"
                :value="filterNode.pattern"
                @input="onFilterPatternChange(idx, $event.target.value)"
                :placeholder="t('aiChatAgentSetup.advanced.responseFilterRegexPatternPlaceholder', 'e.g. \\[debug\\].*')"
                aria-label="Regex pattern to remove"
              />
            </div>
            <div class="form-control max-w-xs">
              <label class="label">
                <span class="label-text">{{ t('aiChatAgentSetup.advanced.responseFilterRegexFlagsLabel', 'Regex flags') }}</span>
              </label>
              <input
                type="text"
                class="input input-bordered"
                maxlength="6"
                :value="filterNode.flags"
                @input="onFilterFlagsChange(idx, $event.target.value)"
                :placeholder="t('aiChatAgentSetup.advanced.responseFilterRegexFlagsPlaceholder', 'e.g. gi')"
                aria-label="Regex flags"
              />
            </div>
          </template>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
import { ref, watch } from 'vue';
import { useI18n } from '@/i18n';
import { Icon } from '@iconify/vue/dist/offline';

const props = defineProps({
  modelValue: { type: Array, required: false, default: () => [] },
  allowReplaceStringFilter: { type: Boolean, required: false, default: false }
});

const emit = defineEmits(['update:modelValue']);
const { t } = useI18n();

function normalizeFilterNode(node) {
  const type = node?.type === 'removeRegex'
    ? 'removeRegex'
    : (node?.type === 'replaceString' ? 'replaceString' : 'removeString');
  const legacyChar = typeof node?.char === 'string' ? node.char : '';
  const textValue = typeof node?.text === 'string' ? node.text : legacyChar;
  const replaceFromValue = typeof node?.replaceFrom === 'string'
    ? node.replaceFrom
    : (typeof node?.from === 'string' ? node.from : '');
  const replaceToValue = typeof node?.replaceTo === 'string'
    ? node.replaceTo
    : (typeof node?.to === 'string' ? node.to : '');
  return {
    type,
    text: type === 'removeString' ? textValue : '',
    replaceFrom: type === 'replaceString' ? replaceFromValue : '',
    replaceTo: type === 'replaceString' ? replaceToValue : '',
    pattern: typeof node?.pattern === 'string' ? node.pattern : '',
    flags: typeof node?.flags === 'string' ? node.flags : ''
  };
}

function normalizeFilters(value) {
  const src = Array.isArray(value) === true ? value : [];
  const out = [];
  for (let i = 0; i < src.length; i = i + 1) {
    out.push(normalizeFilterNode(src[i]));
  }
  return out;
}

const filtersState = ref(normalizeFilters(props.modelValue));

watch(
  () => props.modelValue,
  (val) => {
    filtersState.value = normalizeFilters(val);
  },
  { deep: true }
);

function emitFilters(next) {
  const normalized = normalizeFilters(next);
  filtersState.value = normalized;
  emit('update:modelValue', normalized);
}

function addFilter() {
  const next = normalizeFilters(filtersState.value).concat([{ type: 'removeString', text: '', replaceFrom: '', replaceTo: '', pattern: '', flags: '' }]);
  emitFilters(next);
}

function removeFilter(index) {
  const current = normalizeFilters(filtersState.value);
  const next = [];
  for (let i = 0; i < current.length; i = i + 1) {
    if (i !== index) {
      next.push(current[i]);
    }
  }
  emitFilters(next);
}

function onFilterTypeChange(index, value) {
  const current = normalizeFilters(filtersState.value);
  if (index < 0 || index >= current.length) return;
  let nextType = 'removeString';
  if (value === 'removeRegex') {
    nextType = 'removeRegex';
  } else if (value === 'replaceString' && props.allowReplaceStringFilter === true) {
    nextType = 'replaceString';
  }
  const row = current[index];
  current[index] = {
    type: nextType,
    text: nextType === 'removeString' ? (typeof row.text === 'string' ? row.text : '') : '',
    replaceFrom: nextType === 'replaceString' ? (typeof row.replaceFrom === 'string' ? row.replaceFrom : '') : '',
    replaceTo: nextType === 'replaceString' ? (typeof row.replaceTo === 'string' ? row.replaceTo : '') : '',
    pattern: nextType === 'removeRegex' ? (typeof row.pattern === 'string' ? row.pattern : '') : '',
    flags: nextType === 'removeRegex' ? (typeof row.flags === 'string' ? row.flags : '') : ''
  };
  emitFilters(current);
}

function onFilterTextChange(index, value) {
  const current = normalizeFilters(filtersState.value);
  if (index < 0 || index >= current.length) return;
  current[index] = { ...current[index], text: typeof value === 'string' ? value : '' };
  emitFilters(current);
}

function onFilterPatternChange(index, value) {
  const current = normalizeFilters(filtersState.value);
  if (index < 0 || index >= current.length) return;
  current[index] = { ...current[index], pattern: typeof value === 'string' ? value : '' };
  emitFilters(current);
}

function onFilterFlagsChange(index, value) {
  const current = normalizeFilters(filtersState.value);
  if (index < 0 || index >= current.length) return;
  current[index] = { ...current[index], flags: typeof value === 'string' ? value : '' };
  emitFilters(current);
}

function onFilterReplaceFromChange(index, value) {
  const current = normalizeFilters(filtersState.value);
  if (index < 0 || index >= current.length) return;
  current[index] = { ...current[index], replaceFrom: typeof value === 'string' ? value : '' };
  emitFilters(current);
}

function onFilterReplaceToChange(index, value) {
  const current = normalizeFilters(filtersState.value);
  if (index < 0 || index >= current.length) return;
  current[index] = { ...current[index], replaceTo: typeof value === 'string' ? value : '' };
  emitFilters(current);
}
</script>
